import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Link2, BarChart3, CreditCard, ArrowRight, X, Sparkles } from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Welcome to OnPoint BI",
    subtitle: "Let's get you set up in 60 seconds",
    Icon: Sparkles,
    content: "OnPoint BI brings together your financial, CRM, and banking data into one powerful dashboard with AI-driven insights.",
  },
  {
    id: "connect",
    title: "Connect Your Data",
    subtitle: "Link your first data source",
    Icon: Link2,
    content: "Connect QuickBooks, HubSpot, Salesforce, or Plaid to start populating your dashboard with real-time data.",
    action: "/integrations",
    actionLabel: "Go to Integrations",
  },
  {
    id: "explore",
    title: "Explore Your Dashboard",
    subtitle: "See your KPIs at a glance",
    Icon: BarChart3,
    content: "Your dashboard shows revenue, expenses, customers, and cash flow with trend analysis and AI forecasting.",
    action: "/dashboard",
    actionLabel: "View Dashboard",
  },
  {
    id: "plan",
    title: "Choose Your Plan",
    subtitle: "Unlock premium features",
    Icon: CreditCard,
    content: "Start with the free trial and upgrade anytime to access more integrations, advanced forecasting, and custom reports.",
    action: "/subscriptions",
    actionLabel: "View Plans",
  },
];

export default function OnboardingWizard({ onComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState([]);

  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    setCompleted((c) => [...new Set([...c, STEPS[step].id])]);
    if (isLast) {
      localStorage.setItem("onpoint_onboarded", "true");
      onComplete();
    } else {
      setStep(step + 1);
    }
  };

  const handleAction = () => {
    localStorage.setItem("onpoint_onboarded", "true");
    onComplete();
    if (currentStep.action) navigate(currentStep.action);
  };

  const handleSkip = () => {
    localStorage.setItem("onpoint_onboarded", "true");
    onComplete();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: "rgba(28,32,64,0.6)", backdropFilter: "blur(4px)" }}>
      <div data-testid="onboarding-wizard" className="bg-white rounded-2xl w-full max-w-lg overflow-hidden animate-fade-in"
        style={{ boxShadow: "0 20px 60px rgba(28,32,64,0.25)" }}>
        {/* Progress bar */}
        <div className="flex gap-1 p-3 pb-0">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex-1 h-1 rounded-full transition-all"
              style={{ backgroundColor: i <= step ? "#1B75BA" : "#CDD2DE" }} />
          ))}
        </div>

        {/* Skip button */}
        <div className="flex justify-end px-5 pt-3">
          <button data-testid="onboarding-skip" onClick={handleSkip}
            className="text-xs font-medium flex items-center gap-1 px-2 py-1 rounded-md transition-colors hover:bg-gray-50"
            style={{ color: "#9BA4B8" }}>
            Skip <X className="w-3 h-3" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 pt-2 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "#E8F3FA" }}>
            <currentStep.Icon className="w-8 h-8" style={{ color: "#1B75BA" }} />
          </div>

          <h2 className="text-lg font-bold mb-1" style={{ color: "#1C2040" }}>{currentStep.title}</h2>
          <p className="text-sm mb-4" style={{ color: "#6B7491" }}>{currentStep.subtitle}</p>
          <p className="text-sm leading-relaxed mb-6 max-w-md mx-auto" style={{ color: "#6B7491" }}>{currentStep.content}</p>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 mb-6">
            {STEPS.map((s, i) => (
              <button key={s.id} onClick={() => setStep(i)}
                className="flex items-center gap-1.5 text-xs font-medium transition-all"
                style={{ color: i === step ? "#1B75BA" : completed.includes(s.id) ? "#D4A017" : "#9BA4B8" }}>
                {completed.includes(s.id)
                  ? <CheckCircle className="w-4 h-4" style={{ color: "#D4A017" }} />
                  : <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                      style={{
                        backgroundColor: i === step ? "#1B75BA" : "#CDD2DE",
                        color: i === step ? "white" : "#6B7491"
                      }}>{i + 1}</span>
                }
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {currentStep.action && (
              <button data-testid={`onboarding-action-${currentStep.id}`}
                onClick={handleAction}
                className="flex-1 h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                style={{ border: "1.5px solid #CDD2DE", color: "#1C2040" }}>
                {currentStep.actionLabel}
              </button>
            )}
            <button data-testid="onboarding-next" onClick={handleNext}
              className="flex-1 h-11 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2"
              style={{ backgroundColor: "#1B75BA" }}>
              {isLast ? "Get Started" : "Next"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
